import { isNone } from '@ember/utils';
import { SerializedLegacyAttributeFilters } from 'cio/types/conditions/legacy-filter';
import { FormTriggeredCampaignAttributeFilters } from 'cio/models/campaign';
import GroupCondition from 'cio/app-models/conditions/group';
import { filterOperator as filterEventOperator } from 'cio/utils/conditions/deserialize/event';
import { deserializeFilters as deserializeFormFilters } from 'cio/utils/conditions/deserialize/form';

export default function deserializeAndNormalizeFormAttributeFilters(
  filters: SerializedLegacyAttributeFilters,
): FormTriggeredCampaignAttributeFilters {
  if (isNone(filters)) {
    return GroupCondition.create({ operator: 'or' }) as FormTriggeredCampaignAttributeFilters;
  }

  let children = deserializeFormFilters(filters);
  let operator = filterEventOperator(filters);

  // normalize children 1: if there is only one AND condition, it's a single form trigger with event data filters
  // needs to be wrapped in an OR group condition:
  // { and: [formCondition, ...dataFilterConditions] } -> { or: [{ and: [formCondition, ...dataFilterConditions ] }] }
  if (children.length > 1 && operator === 'and') {
    children = [
      GroupCondition.create({
        operator: 'and',
        children,
      }),
    ];
  }

  // normalize children 2: every form trigger condition should be a group AND condition if it isn't already:
  // { and: [formCondition, ...dataFilterConditions] }
  children = children.map((c) =>
    c instanceof GroupCondition
      ? c
      : GroupCondition.create({
          operator: 'and',
          children: [c],
        }),
  );

  // normalize children 3: data filter conditions should be a single group condition
  // { and: [formCondition, { and/or: [...dataFilterConditions] } }
  children = children.map((c: GroupCondition) => {
    if (c.children.length === 2 && c.children[1] instanceof GroupCondition) {
      return c;
    }

    return GroupCondition.create({
      operator: 'and',
      children: [
        c.children[0],
        c.children[1]
          ? GroupCondition.create({
              operator: 'and',
              children: [c.children[1]],
            })
          : null,
      ].compact(),
    });
  });

  return GroupCondition.create({
    operator: 'or',
    children,
  }) as FormTriggeredCampaignAttributeFilters;
}
